/**
 * Pure, DOM-free helpers shared by the SDK. Kept separate so they can be
 * unit-tested under node (jest) without a browser/jsdom environment.
 */

export const MESSAGE_SOURCE = 'pavel-auth';

export interface User {
  sub: string;
  email: string;
  emailVerified?: boolean;
  emailVerificationAssertion?: 'none' | 'google_gmail' | 'google_workspace' | 'google_non_authoritative';
  emailVerificationObservedAt?: string;
  googleHostedDomain?: string;
  name?: string;
  picture?: string;
}

export interface ParsedFragment {
  accessToken: string;
  refreshToken: string | null;
  expiresIn: number;
  state: string | null;
}

export interface AuthMessage {
  source: typeof MESSAGE_SOURCE;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  state?: string;
}

/** Parse `access_token=...&refresh_token=...` from a URL hash fragment. */
export function parseFragment(hash: string): ParsedFragment | null {
  const raw = hash.charAt(0) === '#' ? hash.slice(1) : hash;
  if (!raw) return null;
  const p = new URLSearchParams(raw);
  const access = p.get('access_token');
  if (!access) return null;
  const expires = Number(p.get('expires_in'));
  return {
    accessToken: access,
    refreshToken: p.get('refresh_token'),
    expiresIn: Number.isFinite(expires) ? expires : 0,
    state: p.get('state'),
  };
}

/**
 * A postMessage is trusted only if it originates from the auth service origin
 * AND carries our source marker. Both checks are required.
 */
export function isTrustedMessage(
  data: unknown,
  origin: string,
  expectedOrigin: string,
): data is AuthMessage {
  if (origin !== expectedOrigin) return false;
  if (typeof data !== 'object' || data === null) return false;
  const m = data as Record<string, unknown>;
  return m.source === MESSAGE_SOURCE && typeof m.access_token === 'string';
}

/** base64url-decode to a UTF-8 string using whatever runtime primitive exists. */
function b64urlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
  const padded = b64 + pad;
  const g = globalThis as { atob?: (s: string) => string };
  if (typeof g.atob === 'function') return g.atob(padded);
  // Node fallback (used in tests if atob is unavailable).
  return Buffer.from(padded, 'base64').toString('binary');
}

/** Decode (without verifying) the JWT payload into a User for display. */
export function decodeUser(accessToken: string | null): User | null {
  if (!accessToken) return null;
  const parts = accessToken.split('.');
  if (parts.length < 2) return null;
  try {
    const part = parts[1] as string;
    const json = b64urlDecode(part);
    const claims = JSON.parse(json) as Record<string, unknown>;
    if (typeof claims.sub !== 'string' || typeof claims.email !== 'string') return null;
    const user: User = { sub: claims.sub, email: claims.email };
    const assertion = claims.email_verification_assertion;
    const observedAt = claims.email_verification_observed_at;
    const hd = claims.google_hosted_domain;
    const separator = claims.email.lastIndexOf(String.fromCharCode(64));
    const domain = claims.email.slice(separator + 1).toLowerCase();
    const authoritative =
      (assertion === 'google_gmail' && domain === 'gmail.com' && hd === undefined) ||
      (assertion === 'google_workspace' && typeof hd === 'string' &&
       domain === hd.toLowerCase());
    if (claims.email_verified === false &&
        (assertion === 'none' || assertion === 'google_non_authoritative')) {
      user.emailVerified = false;
      user.emailVerificationAssertion = assertion;
    }
    if (claims.email_verified === true && authoritative &&
        typeof observedAt === 'string' && !Number.isNaN(Date.parse(observedAt))) {
      user.emailVerified = true;
      user.emailVerificationAssertion = assertion;
      user.emailVerificationObservedAt = observedAt;
      if (typeof hd === 'string') user.googleHostedDomain = hd;
    } else if (claims.email_verified === true) {
      user.emailVerified = false;
    }
    if (typeof claims.name === 'string') user.name = claims.name;
    if (typeof claims.picture === 'string') user.picture = claims.picture;
    return user;
  } catch {
    return null;
  }
}
